import { webPrefix } from "@pontimed/types";
import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ExpandableRow } from "@/components/ui/responsive-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { AdminCountryPills } from "@/components/admin/country-pills";
import { RetiredBadge } from "@/components/admin/retired-badge";
import { useAdminCountries } from "@/hooks/use-admin-countries";
import { useAdminFetch } from "@/hooks/use-admin-fetch";
import { Pagination } from "@/components/ui/pagination";
import { useGradeLabels } from "@/lib/provider-grade-labels";
import { showToast } from "@/lib/ui/toast";
import { useAdminContext } from "@/lib/admin-context";
import { getWebUrl } from "@/lib/web-url";

/**
 * Admin → Providers.
 *
 * Port of src/app/admin/providers/page.tsx (legacy NextJS, ~1700 LOC) per D21
 * NO REINVENTION. Preserves table, review modal (credentials/verification docs/
 * CMR lookup/facilities/services/specialties/contact/activity timeline/notify),
 * document preview lightbox, action buttons (approve/reactivate/suspend/
 * delete/cancel-deletion), specialty per-row approve/reject, and deep-link
 * support for `?review=<id>&tab=specialties`.
 *
 * Optimizations declared (Architecture Discipline Rule 1 + D4):
 *   - raw fetch() → adminFetch
 *   - next-intl useTranslations → Lingui Trans/t
 *   - next/navigation useSearchParams/useRouter → TanStack Router useSearch/useNavigate
 *   - useState/useEffect for fetch → useQuery (shared cache, auto-refetch)
 *   - showToast from legacy src/lib/ui/toast.ts → imperative toast helper
 *     (apps/admin/src/lib/ui/toast.ts — 1:1 port)
 *
 * Endpoints consumed (apps/api):
 *   GET    /api/admin/providers                            (list + stats)
 *   GET    /api/admin/providers/:id                        (detail)
 *   GET    /api/admin/providers/:id/audit-log
 *   GET    /api/admin/providers/cmr-lookup?name=&bestMatch=true
 *   POST   /api/admin/providers/:id/approve
 *   POST   /api/admin/providers/:id/reactivate
 *   POST   /api/admin/providers/:id/suspend
 *   POST   /api/admin/providers/:id/request-update
 *   POST   /api/admin/providers/:id/cancel-deletion
 *   PATCH  /api/admin/providers/:id/specialties/:specialtyId
 *   DELETE /api/admin/providers/:id
 *
 *   GET    /api/admin/providers/:id/document/:i/file (encrypted blob streamer)
 *
 * Encrypted verification docs (non-empty `verificationDocumentDeks[i]`) are
 * streamed through `adminFetch` into object URLs rather than linked directly:
 * `<img src>` / `<iframe src>` cannot carry the `X-Admin-DB` header the admin
 * fetch interceptor injects, and a relative `/api/...` on admin.pontimed.app
 * resolves to the SPA shell because the API lives on api.pontimed.app.
 * Plaintext doc URLs are absolute blob-storage links and are used as-is.
 */

interface ProviderRow {
  id: string;
  firstName: string;
  lastName: string;
  specialtyCode: string | null;
  grade: string | null;
  gender?: string | null;
  licenseNo: string | null;
  primaryCity: string | null;
  primaryCountry: string;
  isVerified: boolean;
  profileStatus: string;
  planTier: string;
  ratingAvg: string | number | null;
  reviewCount: number;
  appointmentCount: number;
  status: string;
  isClaimed: boolean;
  createdAt: string;
  _count?: { facilityProviders: number };
  pendingDeletion: { scheduledHardDeleteAt: string; daysRemaining: number } | null;
}

interface ProviderDetail {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  nameAscii: string | null;
  photoUrl: string | null;
  bio: Record<string, string> | null;
  grade: string | null;
  specialtyCode: string | null;
  specialty: { name: Record<string, string>; code: string } | null;
  licenseNo: string | null;
  university: string | null;
  graduationYear: number | null;
  yearsExperience: number | null;
  phones: unknown;
  emails: string[];
  languages: string[];
  gender: string | null;
  primaryCity: string | null;
  primaryCountry: string;
  isVerified: boolean;
  verifiedAt: string | null;
  verificationMethod: string | null;
  verificationDocuments: string[];
  verificationDocumentDeks?: string[];
  profileStatus: string;
  listingType: string;
  planTier: string;
  offersVideoConsultation: boolean;
  acceptsNewPatients: boolean;
  ratingAvg: string | number | null;
  reviewCount: number;
  dataSources: unknown;
  status: string;
  createdAt: string;
  updatedAt: string;
  facilityProviders: {
    role: string;
    specialtyHere: string | null;
    facility: {
      id: string;
      name: Record<string, string>;
      addressFormatted: string | null;
      addressCity: string;
      category: string;
    };
  }[];
  providerServices: {
    price: string | number | null;
    currency: string;
    acceptsCas: boolean;
    service: { name: Record<string, string>; code: string };
  }[];
  insuranceProviders: {
    insurance: { name: Record<string, string>; type: string };
  }[];
  providerSpecialties: {
    specialtyId: string;
    isPrimary: boolean;
    approvalStatus: "pending" | "approved" | "rejected";
    verifiedAt: string | null;
    verifiedBy: string | null;
    rejectionReason: string | null;
    createdAt: string;
    specialty: {
      id: string;
      code: string;
      name: Record<string, string>;
      type: string | null;
    };
  }[];
}

interface AuditLogEntry {
  id: string;
  providerId: string;
  action: string;
  actorType: string;
  actorId: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

interface CmrResult {
  fullName: string;
  specialty: string;
  licenseNo: string;
  grade: string;
  city: string;
  county: string;
  hasActiveLicense: boolean;
}

interface ProvidersStats {
  total: number;
  verified: number;
  pending: number;
  draft: number;
  published: number;
  suspended: number;
  pendingDeletion?: number;
  avgRating: string;
}

const AUDIT_ACTION_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  registered: { label: "Registered", color: "text-blue-600 bg-blue-100", icon: "+" },
  profile_updated: { label: "Profile Updated", color: "text-indigo-600 bg-indigo-100", icon: "✎" },
  submitted_for_review: {
    label: "Submitted for Review",
    color: "text-amber-600 bg-amber-100",
    icon: "→",
  },
  documents_uploaded: {
    label: "Documents Uploaded",
    color: "text-cyan-600 bg-cyan-100",
    icon: "↑",
  },
  admin_action_required: {
    label: "Action Required Sent",
    color: "text-orange-600 bg-orange-100",
    icon: "!",
  },
  admin_approved: { label: "Approved", color: "text-emerald-600 bg-emerald-100", icon: "✓" },
  admin_suspended: { label: "Suspended", color: "text-red-600 bg-red-100", icon: "⊘" },
  admin_reactivated: { label: "Reactivated", color: "text-violet-600 bg-violet-100", icon: "↻" },
  admin_deleted: { label: "Deleted", color: "text-red-700 bg-red-100", icon: "✕" },
  notification_sent: { label: "Notification Sent", color: "text-sky-600 bg-sky-100", icon: "✉" },
};

const NOTIFY_REASON_OPTIONS = [
  { key: "missing_documents", label: "Missing documents" },
  { key: "illegible_documents", label: "Illegible documents" },
  { key: "incorrect_data", label: "Incorrect data" },
  { key: "incomplete_profile", label: "Incomplete profile" },
  { key: "photo_required", label: "Photo required" },
  { key: "license_mismatch", label: "License mismatch" },
];

type ProfileStatus = "all" | "draft" | "pending_review" | "published" | "suspended";
type VerifiedFilter = "all" | "verified" | "not_verified";
type DeletedFilter = "hide" | "all" | "only" | "pending" | "retired";

const STATUS_VALUES: ProfileStatus[] = ["all", "pending_review", "published", "draft", "suspended"];
const DELETED_VALUES: DeletedFilter[] = ["hide", "all", "only", "pending", "retired"];
const VERIFIED_VALUES: VerifiedFilter[] = ["all", "verified", "not_verified"];

const statusBadgeClass: Record<string, string> = {
  draft: "bg-zinc-100 text-zinc-600",
  pending_review: "bg-amber-100 text-amber-700",
  published: "bg-emerald-100 text-emerald-700",
  suspended: "bg-red-100 text-red-700",
};

interface ProvidersSearch {
  source?: string;
  country?: string;
  review?: string;
  tab?: string;
}

export const Route = createFileRoute("/_authenticated/providers")({
  validateSearch: (search: Record<string, unknown>): ProvidersSearch => ({
    source: typeof search.source === "string" ? search.source : undefined,
    country: typeof search.country === "string" ? search.country : undefined,
    review: typeof search.review === "string" ? search.review : undefined,
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: ProvidersPage,
});

function ProvidersPage() {
  const { t, i18n } = useLingui();
  // Admin sees the grade facts whatever the profession (no profession filter).
  const { formatGrade } = useGradeLabels();
  const { adminFetch } = useAdminFetch();
  // The row comes from the DB the toggle points at; its profile lives on that site.
  const { dbTarget } = useAdminContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { visibleCountries: countries } = useAdminCountries();

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
    isClaimed: boolean;
  } | null>(null);
  const [erasureRequested, setErasureRequested] = useState(false);

  const [sourceFilter, setSourceFilter] = useState<string>(search.source ?? "");
  const [selectedCountry, setSelectedCountry] = useState<string>(search.country ?? "");
  const [statusFilter, setStatusFilter] = useState<ProfileStatus>("all");
  const [deletedFilter, setDeletedFilter] = useState<DeletedFilter>("hide");
  const [verifiedFilter, setVerifiedFilter] = useState<VerifiedFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 50;

  const [reviewProvider, setReviewProvider] = useState<ProviderDetail | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const specialtiesSectionRef = useRef<HTMLDivElement | null>(null);
  const deepLinkTabRef = useRef<string | null>(null);
  const [cmrResult, setCmrResult] = useState<CmrResult | null>(null);
  const [cmrLoading, setCmrLoading] = useState(false);
  const [docPreview, setDocPreview] = useState<{ url: string; isPdf: boolean } | null>(null);
  const [docObjectUrls, setDocObjectUrls] = useState<Record<number, string>>({});

  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);

  const [notifyReasons, setNotifyReasons] = useState<Set<string>>(new Set());
  const [notifyCustomMsg, setNotifyCustomMsg] = useState("");
  const [notifySending, setNotifySending] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState(false);
  const [notifyPanelOpen, setNotifyPanelOpen] = useState(false);
  const [notifyError, setNotifyError] = useState<string | null>(null);

  const notifyReasonsRef = useRef(notifyReasons);
  notifyReasonsRef.current = notifyReasons;
  const notifyCustomMsgRef = useRef(notifyCustomMsg);
  notifyCustomMsgRef.current = notifyCustomMsg;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const buildQS = (extra: Record<string, string | undefined>): string => {
    const sp = new URLSearchParams();
    if (selectedCountry) sp.set("country", selectedCountry);
    if (sourceFilter) sp.set("source", sourceFilter);
    if (deletedFilter !== "hide") sp.set("deleted", deletedFilter);
    if (debouncedSearch) sp.set("search", debouncedSearch);
    for (const [k, v] of Object.entries(extra)) {
      if (v) sp.set(k, v);
    }
    return sp.toString();
  };

  // Reset pagination when filters change.
  useEffect(() => {
    setOffset(0);
  }, [selectedCountry, sourceFilter, statusFilter, deletedFilter, verifiedFilter, debouncedSearch]);

  const providersQuery = useQuery({
    queryKey: [
      "admin",
      "providers",
      "list",
      selectedCountry,
      sourceFilter,
      statusFilter,
      deletedFilter,
      verifiedFilter,
      debouncedSearch,
      offset,
    ],
    queryFn: async (): Promise<{ providers: ProviderRow[]; total: number }> => {
      try {
        const data = await adminFetch.get<{ providers: ProviderRow[]; total: number }>(
          `/api/admin/providers?${buildQS({
            status: statusFilter !== "all" ? statusFilter : undefined,
            verified:
              verifiedFilter === "verified"
                ? "true"
                : verifiedFilter === "not_verified"
                  ? "false"
                  : undefined,
            limit: String(PAGE_SIZE),
            offset: String(offset),
          })}`,
        );
        return { providers: data.providers || [], total: data.total || 0 };
      } catch (error) {
        if (error instanceof ApiError) return { providers: [], total: 0 };
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const statsQuery = useQuery({
    queryKey: ["admin", "providers", "stats", selectedCountry, debouncedSearch, deletedFilter],
    queryFn: async (): Promise<{
      stats: ProvidersStats;
      countryCounts: Record<string, number>;
    } | null> => {
      try {
        const data = await adminFetch.get<{
          stats: ProvidersStats;
          countryCounts: Record<string, number>;
        }>(`/api/admin/providers?${buildQS({ stats: "true" })}`);
        return { stats: data.stats, countryCounts: data.countryCounts ?? {} };
      } catch (error) {
        if (error instanceof ApiError) return null;
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const providers = providersQuery.data?.providers ?? [];
  const total = providersQuery.data?.total ?? 0;
  const globalStats: ProvidersStats = statsQuery.data?.stats ?? {
    total: 0,
    verified: 0,
    pending: 0,
    draft: 0,
    published: 0,
    suspended: 0,
    avgRating: "0.0",
  };
  const countryCounts = statsQuery.data?.countryCounts ?? {};

  const refreshAll = useCallback(() => {
    void providersQuery.refetch();
    void statsQuery.refetch();
  }, [providersQuery, statsQuery]);

  const fetchAuditLog = useCallback(
    async (providerId: string) => {
      setAuditLoading(true);
      try {
        const data = await adminFetch.get<{ logs: AuditLogEntry[] }>(
          `/api/admin/providers/${providerId}/audit-log`,
        );
        setAuditLogs(data.logs || []);
      } catch {
        setAuditLogs([]);
      } finally {
        setAuditLoading(false);
      }
    },
    [adminFetch],
  );

  const sendNotification = useCallback(
    async (providerId: string) => {
      const reasons = Array.from(notifyReasonsRef.current);
      const customMsg = notifyCustomMsgRef.current.trim();
      if (reasons.length === 0 && !customMsg) return;

      setNotifySending(true);
      setNotifySuccess(false);
      setNotifyError(null);
      try {
        await adminFetch.post(`/api/admin/providers/${providerId}/request-update`, {
          reasons,
          customMessage: customMsg || undefined,
        });
        setNotifySuccess(true);
        setNotifyReasons(new Set());
        setNotifyCustomMsg("");
        void fetchAuditLog(providerId);
        setTimeout(() => setNotifySuccess(false), 3000);
      } catch (e) {
        if (e instanceof ApiError) {
          setNotifyError(
            (typeof e.body === "object" && e.body && "error" in e.body
              ? (e.body as { error?: string }).error
              : null) ?? `Failed (${e.status})`,
          );
        } else {
          setNotifyError(e instanceof Error ? e.message : "Network error");
        }
      } finally {
        setNotifySending(false);
      }
    },
    [adminFetch, fetchAuditLog],
  );

  const openReview = useCallback(
    async (providerId: string) => {
      setReviewLoading(true);
      setReviewProvider(null);
      setCmrResult(null);
      setAuditLogs([]);
      setTimelineOpen(false);
      setNotifyReasons(new Set());
      setNotifyCustomMsg("");
      setNotifySuccess(false);
      setNotifyError(null);
      setNotifyPanelOpen(false);
      try {
        const data = await adminFetch.get<ProviderDetail>(`/api/admin/providers/${providerId}`);
        setReviewProvider(data);
        if (data.primaryCountry === "RO") {
          setCmrLoading(true);
          const name = `${data.firstName} ${data.lastName}`;
          adminFetch
            .get<{ result?: CmrResult }>(
              `/api/admin/providers/cmr-lookup?name=${encodeURIComponent(name)}&bestMatch=true`,
            )
            .then((d) => {
              if (d?.result) setCmrResult(d.result);
            })
            .catch(() => {
              /* CMR offline — surfaced inline */
            })
            .finally(() => setCmrLoading(false));
        }
        void fetchAuditLog(data.id);
      } catch {
        // 404 / 500 — leave modal closed
      } finally {
        setReviewLoading(false);
      }
    },
    [adminFetch, fetchAuditLog],
  );

  // Deep-link: ?review=<id>&tab=specialties opens modal once.
  useEffect(() => {
    const reviewId = search.review;
    if (!reviewId) return;
    if (reviewProvider?.id === reviewId) return;
    deepLinkTabRef.current = search.tab ?? null;
    void openReview(reviewId);
    void navigate({
      search: (prev) => ({
        ...prev,
        review: undefined,
        tab: undefined,
      }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.review, search.tab]);

  useEffect(() => {
    if (deepLinkTabRef.current !== "specialties") return;
    if (!reviewProvider) return;
    const el = specialtiesSectionRef.current;
    if (!el) return;
    deepLinkTabRef.current = null;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [reviewProvider]);

  // ── Encrypted verification documents → object URLs ──────────────────────
  // Envelope-encrypted docs only exist behind the decrypting stream endpoint
  // on apps/api, and neither `<img src>` nor `<iframe src>` can send the
  // `X-Admin-DB` header (nor reach the API origin from a relative path), so
  // pull the bytes with adminFetch and render blobs. Runs on every review-modal
  // open and on every Local↔Prod switch (`adminFetch` identity flips).
  useEffect(() => {
    const docs = reviewProvider?.verificationDocuments ?? [];
    const deks = reviewProvider?.verificationDocumentDeks ?? [];
    const encrypted = docs.map((_, i) => i).filter((i) => !!deks[i]);
    if (!reviewProvider || encrypted.length === 0) {
      setDocObjectUrls({});
      return;
    }
    let cancelled = false;
    const created: string[] = [];
    void (async () => {
      const resolved: Record<number, string> = {};
      for (const index of encrypted) {
        try {
          const res = await adminFetch.raw(
            `/api/admin/providers/${reviewProvider.id}/document/${index}/file`,
            { method: "GET" },
          );
          if (!res.ok) continue;
          const objectUrl = URL.createObjectURL(await res.blob());
          created.push(objectUrl);
          resolved[index] = objectUrl;
        } catch {
          // Leave the slot unresolved — the tile renders a placeholder.
        }
      }
      if (cancelled) {
        created.forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      setDocObjectUrls(resolved);
    })();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [reviewProvider, adminFetch]);

  const approveProvider = useCallback(
    async (providerId: string) => {
      setActionLoading(providerId);
      try {
        // Plan D6: one action approves the profile AND its primary specialty.
        // The response says whether the second half actually happened — a
        // provider published without an approved primary specialty fails the
        // Meili indexability gate and never shows up in search, which is
        // invisible from this screen unless we say it.
        const data = await adminFetch.post<{
          specialtyApproved?: boolean;
          warnings?: string[];
        }>(`/api/admin/providers/${providerId}/approve`);
        if (data.warnings?.includes("no_primary_specialty")) {
          showToast(
            t`Approved — no primary specialty, so this doctor will not appear in search.`,
            "info",
          );
        } else if (data.warnings?.includes("specialty_approval_failed")) {
          showToast(
            t`Approved, but the primary specialty could not be approved. Approve it from the specialties list.`,
            "info",
          );
        } else if (data.specialtyApproved) {
          showToast(t`Approved — profile and primary specialty.`, "success");
        } else {
          showToast(t`Approved.`, "success");
        }
        refreshAll();
      } catch (e) {
        if (e instanceof ApiError) {
          const msg =
            (typeof e.body === "object" && e.body && "error" in e.body
              ? (e.body as { error?: string }).error
              : null) ?? "Unknown error";
          showToast(t`Approve failed: ${msg}`, "error");
        } else {
          showToast(t`Approve request failed`, "error");
        }
      } finally {
        setActionLoading(null);
      }
    },
    [adminFetch, refreshAll, t],
  );

  const reactivateProvider = useCallback(
    async (providerId: string) => {
      setActionLoading(providerId);
      try {
        await adminFetch.post(`/api/admin/providers/${providerId}/reactivate`);
        refreshAll();
      } catch {
        /* silent */
      } finally {
        setActionLoading(null);
      }
    },
    [adminFetch, refreshAll],
  );

  const suspendProvider = useCallback(
    async (providerId: string) => {
      if (!confirm(t`Are you sure you want to suspend this provider?`)) return;
      setActionLoading(providerId);
      try {
        await adminFetch.post(`/api/admin/providers/${providerId}/suspend`);
        refreshAll();
      } catch {
        /* silent */
      } finally {
        setActionLoading(null);
      }
    },
    [adminFetch, refreshAll, t],
  );

  const [specialtyActionId, setSpecialtyActionId] = useState<string | null>(null);

  const decideSpecialty = useCallback(
    async (
      providerId: string,
      specialtyId: string,
      action: "approve" | "reject",
      reason?: string,
    ) => {
      setSpecialtyActionId(`${specialtyId}:${action}`);
      try {
        await adminFetch.patch(`/api/admin/providers/${providerId}/specialties/${specialtyId}`, {
          action,
          reason,
        });
        setReviewProvider((prev) =>
          prev
            ? {
                ...prev,
                providerSpecialties: prev.providerSpecialties.map((s) =>
                  s.specialtyId === specialtyId
                    ? {
                        ...s,
                        approvalStatus: action === "approve" ? "approved" : "rejected",
                        verifiedAt: action === "approve" ? new Date().toISOString() : null,
                        rejectionReason: action === "reject" ? (reason ?? null) : null,
                      }
                    : s,
                ),
              }
            : prev,
        );
      } catch {
        /* silent */
      } finally {
        setSpecialtyActionId(null);
      }
    },
    [adminFetch],
  );

  const deleteProvider = useCallback((providerId: string, name: string, isClaimed: boolean) => {
    setErasureRequested(false);
    setDeleteTarget({ id: providerId, name, isClaimed });
  }, []);

  const confirmDeleteProvider = useCallback(async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    const { id: providerId, isClaimed } = target;
    // Two different deletes behind one button (DELETE /api/admin/providers/:id):
    // an owned profile is queued for 30 days, an unclaimed one is a permanent
    // tombstone that imports skip. Ticked "asked for removal" (unclaimed only):
    // the same erasure as the link in the information email — contacts
    // blocked, data minimised (apps/api lib/provider-erasure.ts).
    const erasure = !isClaimed && erasureRequested;
    setDeleteTarget(null);
    setActionLoading(providerId);
    try {
      const data = await adminFetch.delete<{
        mode?: "soft" | "tombstone" | "hard" | "erased";
        cascadedFacilities?: { name: Record<string, string> }[];
        retentionDays?: number;
      }>(`/api/admin/providers/${providerId}${erasure ? "?erasure=true" : ""}`);
      const names = (data.cascadedFacilities ?? [])
        .map((f) => f.name?.ro || f.name?.en || "—")
        .join(", ");
      if (data.mode === "erased") {
        // cascadedFacilities = the doctor's own solo practices, removed with them.
        showToast(
          names
            ? t`Profile and their own practice (${names}) removed at their request: contacts blocked, data reduced to the minimum`
            : t`Profile removed at their request: contacts blocked, data reduced to the minimum`,
          "success",
        );
      } else if (data.cascadedFacilities?.length) {
        showToast(
          t`Provider queued for deletion (${data.retentionDays}d). Cascaded ${data.cascadedFacilities.length} facility(ies): ${names}`,
          "success",
        );
      } else if (data.mode === "soft") {
        showToast(t`Profile queued for deletion (${data.retentionDays}d)`, "success");
      } else {
        showToast(t`Profile deleted`, "success");
      }
      refreshAll();
    } catch (e) {
      if (e instanceof ApiError) {
        const msg =
          (typeof e.body === "object" && e.body && "error" in e.body
            ? (e.body as { error?: string }).error
            : null) ?? "Unknown error";
        showToast(t`Delete failed: ${msg}`, "error");
        // 503 RETRY: the profile is a tombstone already, a follow-up failed.
        // Only the erasure finishes it, so the dialog comes back still ticked.
        if (erasure && e.status === 503) {
          setErasureRequested(true);
          setDeleteTarget(target);
        }
      } else {
        showToast(t`Delete request failed`, "error");
      }
    } finally {
      setActionLoading(null);
    }
  }, [adminFetch, deleteTarget, erasureRequested, refreshAll, t]);

  const cancelProviderDeletion = useCallback(
    async (providerId: string) => {
      setActionLoading(providerId);
      try {
        await adminFetch.post(`/api/admin/providers/${providerId}/cancel-deletion`);
        refreshAll();
      } catch (e) {
        if (e instanceof ApiError) {
          const msg =
            (typeof e.body === "object" && e.body && "error" in e.body
              ? (e.body as { error?: string }).error
              : null) ?? "Unknown error";
          showToast(t`Cancel failed: ${msg}`, "error");
        } else {
          showToast(t`Cancel request failed`, "error");
        }
      } finally {
        setActionLoading(null);
      }
    },
    [adminFetch, refreshAll, t],
  );

  const statusLabel = (s: ProfileStatus): string => {
    switch (s) {
      case "all":
        return t`All statuses`;
      case "draft":
        return t`Draft`;
      case "pending_review":
        return t`Pending review`;
      case "published":
        return t`Published`;
      case "suspended":
        return t`Suspended`;
    }
  };

  const deletedLabel = (s: DeletedFilter): string => {
    switch (s) {
      case "hide":
        return t`Not deleted`;
      case "all":
        return t`All (incl. deleted)`;
      case "only":
        return t`Only deleted`;
      case "pending":
        return t`Marked for deletion`;
      case "retired":
        return t`Retired (no longer listed by the source)`;
    }
  };

  const verifiedLabelFor = (s: VerifiedFilter): string => {
    switch (s) {
      case "all":
        return t`All verification`;
      case "verified":
        return t`Verified`;
      case "not_verified":
        return t`Not verified`;
    }
  };

  const profileStatusLabel = (s: string): string => {
    if (s === "draft") return t`Draft`;
    if (s === "pending_review") return t`Pending review`;
    if (s === "published") return t`Published`;
    if (s === "suspended") return t`Suspended`;
    return s;
  };

  // Named like the variable the delete texts interpolate, so their translations stay.
  const providerName = deleteTarget?.name ?? "";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Providers</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Doctor profiles, verification queue, claim flows, deletion management.</Trans>
        </p>
      </header>

      <AdminCountryPills
        countries={countries.map((c) => ({ code: c.code, name: c.name, flag: c.flag }))}
        counts={countryCounts}
        selected={selectedCountry}
        onSelect={setSelectedCountry}
      />

      {sourceFilter && (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm">
            <Trans>Source:</Trans>
          </span>
          <Badge variant="secondary" className="gap-1 px-3 py-1 text-sm font-semibold">
            {sourceFilter}
            <button
              className="ml-1 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
              onClick={() => {
                setSourceFilter("");
                void navigate({
                  search: (prev) => ({ ...prev, source: undefined }),
                  replace: true,
                });
              }}
              aria-label={t`Clear source filter`}
            >
              ✕
            </button>
          </Badge>
          <span className="text-muted-foreground text-sm">
            <Trans>{total} records</Trans>
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {(
          [
            {
              label: t`Total`,
              value: globalStats.total,
              bg: "bg-teal-100",
              text: "text-teal-700",
              filter: "all" as ProfileStatus,
              ring: "ring-teal-400",
            },
            {
              label: t`Published`,
              value: globalStats.published,
              bg: "bg-emerald-100",
              text: "text-emerald-700",
              filter: "published" as ProfileStatus,
              ring: "ring-emerald-400",
            },
            {
              label: t`Pending review`,
              value: globalStats.pending,
              bg: "bg-amber-100",
              text: "text-amber-700",
              filter: "pending_review" as ProfileStatus,
              ring: "ring-amber-400",
            },
            {
              label: t`Draft`,
              value: globalStats.draft,
              bg: "bg-zinc-100",
              text: "text-zinc-700",
              filter: "draft" as ProfileStatus,
              ring: "ring-zinc-400",
            },
            {
              label: t`Suspended`,
              value: globalStats.suspended,
              bg: "bg-red-100",
              text: "text-red-700",
              filter: "suspended" as ProfileStatus,
              ring: "ring-red-400",
            },
            {
              label: t`Avg rating`,
              value: globalStats.avgRating,
              bg: "bg-blue-100",
              text: "text-blue-700",
              filter: null,
              ring: "",
            },
          ] as const
        ).map((stat) => (
          <Card
            key={stat.label}
            className={`transition-all ${stat.filter !== null ? "cursor-pointer hover:shadow-md" : ""} ${statusFilter === stat.filter && stat.filter !== "all" ? `ring-2 ${stat.ring}` : ""}`}
            onClick={() => {
              if (stat.filter !== null) setStatusFilter(stat.filter);
            }}
          >
            <CardContent className="flex items-center gap-3 p-4">
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-lg text-lg font-bold ${stat.bg} ${stat.text}`}
              >
                {stat.value}
              </span>
              <div>
                <div className="text-xl font-bold">{stat.value}</div>
                <div className="text-muted-foreground text-xs">{stat.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <Input
          placeholder={t`Search by name…`}
          className="max-w-xs"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="border-border bg-card focus:ring-primary/30 focus:border-primary rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProfileStatus)}
        >
          {STATUS_VALUES.map((value) => (
            <option key={value} value={value}>
              {statusLabel(value)}
            </option>
          ))}
        </select>
        <select
          className="border-border bg-card focus:ring-primary/30 focus:border-primary rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset"
          value={deletedFilter}
          onChange={(e) => setDeletedFilter(e.target.value as DeletedFilter)}
        >
          {DELETED_VALUES.map((value) => (
            <option key={value} value={value}>
              {deletedLabel(value)}
            </option>
          ))}
        </select>
        <select
          className="border-border bg-card focus:ring-primary/30 focus:border-primary rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset"
          value={verifiedFilter}
          onChange={(e) => setVerifiedFilter(e.target.value as VerifiedFilter)}
        >
          {VERIFIED_VALUES.map((value) => (
            <option key={value} value={value}>
              {verifiedLabelFor(value)}
            </option>
          ))}
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          {providersQuery.isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
              <Trans>Loading providers…</Trans>
            </div>
          ) : providers.length === 0 ? (
            <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
              <Trans>No providers match these filters.</Trans>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Trans>Name</Trans>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <Trans>Specialty</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>City</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>Country</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Grade</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>License</Trans>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <Trans>Status</Trans>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <Trans>Verified</Trans>
                  </TableHead>
                  <TableHead className="hidden text-center lg:table-cell">
                    <Trans>Facilities</Trans>
                  </TableHead>
                  <TableHead className="hidden text-right xl:table-cell">
                    <Trans>Rating</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Actions</Trans>
                  </TableHead>
                  <TableHead className="w-8 md:hidden" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.map((doc) => (
                  <ExpandableRow
                    key={doc.id}
                    mobileDetails={[
                      { label: <Trans>Specialty</Trans>, value: doc.specialtyCode || "—" },
                      { label: <Trans>City</Trans>, value: doc.primaryCity || "—" },
                      { label: <Trans>Country</Trans>, value: doc.primaryCountry },
                      {
                        label: <Trans>Grade</Trans>,
                        value: formatGrade(doc.grade, null, doc.gender) || doc.grade || "—",
                      },
                      { label: <Trans>License</Trans>, value: doc.licenseNo || "—" },
                      {
                        label: <Trans>Status</Trans>,
                        value: profileStatusLabel(doc.profileStatus),
                      },
                      {
                        label: <Trans>Verified</Trans>,
                        value: doc.isVerified ? t`Yes` : t`No`,
                      },
                      {
                        label: <Trans>Facilities</Trans>,
                        value: String(doc._count?.facilityProviders || 0),
                      },
                      {
                        label: <Trans>Rating</Trans>,
                        value: doc.ratingAvg
                          ? `${Number(doc.ratingAvg).toFixed(1)} (${doc.reviewCount})`
                          : "—",
                      },
                    ]}
                  >
                    <TableCell className="font-medium">
                      {doc.firstName} {doc.lastName}
                      {doc.status === "retired" && (
                        <div className="mt-1">
                          <RetiredBadge />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {doc.specialtyCode || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden md:table-cell">
                      {doc.primaryCity || "—"}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{doc.primaryCountry}</TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {formatGrade(doc.grade, null, doc.gender) || doc.grade || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden lg:table-cell">
                      {doc.licenseNo || "—"}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex flex-wrap gap-1">
                        <Badge
                          variant="secondary"
                          className={
                            statusBadgeClass[doc.profileStatus] || "bg-zinc-100 text-zinc-600"
                          }
                        >
                          {profileStatusLabel(doc.profileStatus)}
                        </Badge>
                        {doc.pendingDeletion && (
                          <Badge className="bg-red-100 text-xs text-red-700">
                            <Trans>Delete in {doc.pendingDeletion.daysRemaining}d</Trans>
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {doc.isVerified ? (
                        <span className="font-medium text-emerald-600">✓</span>
                      ) : (
                        <span className="font-medium text-red-500">✗</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden text-center lg:table-cell">
                      {doc._count?.facilityProviders || 0}
                    </TableCell>
                    <TableCell className="hidden text-right xl:table-cell">
                      {doc.ratingAvg ? (
                        <span>
                          {Number(doc.ratingAvg).toFixed(1)}{" "}
                          <span className="text-muted-foreground text-xs">({doc.reviewCount})</span>
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {doc.profileStatus === "pending_review" && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 border-amber-300 px-2 text-xs text-amber-700 hover:bg-amber-50"
                              onClick={() => openReview(doc.id)}
                            >
                              <Trans>Review</Trans>
                            </Button>
                            <Button
                              size="sm"
                              className="h-7 bg-teal-600 px-2 text-xs text-white hover:bg-teal-700"
                              disabled={actionLoading === doc.id}
                              onClick={() => approveProvider(doc.id)}
                            >
                              {actionLoading === doc.id ? "..." : <Trans>Approve</Trans>}
                            </Button>
                          </>
                        )}
                        {doc.profileStatus === "suspended" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-teal-300 px-2 text-xs text-teal-600 hover:bg-teal-50"
                            disabled={actionLoading === doc.id}
                            onClick={() => reactivateProvider(doc.id)}
                          >
                            {actionLoading === doc.id ? "..." : <Trans>Reactivate</Trans>}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-red-300 px-2 text-xs text-red-600 hover:bg-red-50"
                            disabled={actionLoading === doc.id}
                            onClick={() => suspendProvider(doc.id)}
                          >
                            <Trans>Suspend</Trans>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() =>
                            window.open(
                              `${getWebUrl(dbTarget)}${webPrefix(doc.primaryCountry.toLowerCase(), i18n.locale)}/provider/${doc.id}`,
                              "_blank",
                            )
                          }
                        >
                          <Trans>View</Trans>
                        </Button>
                        {doc.pendingDeletion ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-emerald-300 px-2 text-xs text-emerald-700 hover:bg-emerald-50"
                            disabled={actionLoading === doc.id}
                            onClick={() => cancelProviderDeletion(doc.id)}
                          >
                            {actionLoading === doc.id ? "..." : <Trans>Cancel deletion</Trans>}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-red-500 hover:bg-red-50 hover:text-red-700"
                            disabled={actionLoading === doc.id}
                            onClick={() =>
                              deleteProvider(
                                doc.id,
                                `${doc.firstName} ${doc.lastName}`,
                                doc.isClaimed,
                              )
                            }
                          >
                            {actionLoading === doc.id ? "..." : <Trans>Delete</Trans>}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </ExpandableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {!providersQuery.isLoading && providers.length > 0 && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={total}
          onChange={(next) => setOffset(next)}
        />
      )}

      {(reviewProvider || reviewLoading) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => {
            setReviewProvider(null);
            setCmrResult(null);
          }}
        >
          <div
            className="bg-card max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {reviewLoading ? (
              <div className="flex items-center justify-center py-12 text-sm">
                <Trans>Loading…</Trans>
              </div>
            ) : reviewProvider ? (
              <div className="space-y-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {reviewProvider.photoUrl ? (
                      <img
                        src={reviewProvider.photoUrl}
                        alt=""
                        className="h-16 w-16 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-teal-100 text-xl font-bold text-teal-700">
                        {reviewProvider.firstName[0]}
                        {reviewProvider.lastName[0]}
                      </div>
                    )}
                    <div>
                      <h2 className="text-lg font-bold">
                        {reviewProvider.firstName} {reviewProvider.middleName || ""}{" "}
                        {reviewProvider.lastName}
                      </h2>
                      <div className="text-muted-foreground text-sm">
                        {formatGrade(reviewProvider.grade, null, reviewProvider.gender) ||
                          reviewProvider.grade ||
                          "—"}{" "}
                        ·{" "}
                        {reviewProvider.specialty?.name?.ro ||
                          reviewProvider.specialty?.name?.en ||
                          reviewProvider.specialtyCode ||
                          "—"}
                      </div>
                      <Badge
                        className={`mt-1 ${
                          statusBadgeClass[reviewProvider.profileStatus] ||
                          "bg-zinc-100 text-zinc-600"
                        }`}
                      >
                        {profileStatusLabel(reviewProvider.profileStatus)}
                      </Badge>
                    </div>
                  </div>
                  <button
                    className="text-muted-foreground hover:text-foreground text-xl"
                    onClick={() => {
                      setReviewProvider(null);
                      setCmrResult(null);
                    }}
                    aria-label={t`Close review`}
                  >
                    ×
                  </button>
                </div>

                <div className="rounded-lg border p-4">
                  <h3 className="mb-2 text-sm font-semibold">
                    <Trans>Credentials</Trans>
                  </h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>License No:</Trans>
                      </span>{" "}
                      <span className="font-medium">{reviewProvider.licenseNo || "—"}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>University:</Trans>
                      </span>{" "}
                      {reviewProvider.university || "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Graduation:</Trans>
                      </span>{" "}
                      {reviewProvider.graduationYear || "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Experience:</Trans>
                      </span>{" "}
                      {reviewProvider.yearsExperience
                        ? t`${reviewProvider.yearsExperience} years`
                        : "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>City:</Trans>
                      </span>{" "}
                      {reviewProvider.primaryCity || "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Country:</Trans>
                      </span>{" "}
                      {reviewProvider.primaryCountry}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Languages:</Trans>
                      </span>{" "}
                      {reviewProvider.languages.join(", ") || "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Gender:</Trans>
                      </span>{" "}
                      {reviewProvider.gender || "—"}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border p-4">
                  <h3 className="mb-2 text-sm font-semibold">
                    <Trans>Verification</Trans>
                  </h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Verified:</Trans>
                      </span>{" "}
                      {reviewProvider.isVerified ? (
                        <span className="font-medium text-emerald-600">
                          <Trans>Yes</Trans>
                        </span>
                      ) : (
                        <span className="font-medium text-red-500">
                          <Trans>No</Trans>
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Method:</Trans>
                      </span>{" "}
                      {reviewProvider.verificationMethod || "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Verified at:</Trans>
                      </span>{" "}
                      {reviewProvider.verifiedAt
                        ? new Date(reviewProvider.verifiedAt).toLocaleDateString()
                        : "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Listing:</Trans>
                      </span>{" "}
                      {reviewProvider.listingType}
                    </div>
                  </div>
                  {reviewProvider.verificationDocuments.length > 0 ? (
                    <div className="mt-3">
                      <span className="text-muted-foreground text-sm">
                        <Trans>Documents ({reviewProvider.verificationDocuments.length}):</Trans>
                      </span>
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        {reviewProvider.verificationDocuments.map((docUrl, i) => {
                          const isPdf = docUrl.endsWith(".pdf");
                          const slotMatch = docUrl.match(/\/([a-z-]+)-[a-f0-9]+\.\w+$/);
                          const slotLabel = slotMatch
                            ? slotMatch[1]
                                .replace(/-/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())
                            : `Document ${i + 1}`;

                          const dek = reviewProvider.verificationDocumentDeks?.[i];
                          // Encrypted docs resolve to a blob once the effect
                          // above has streamed them; plain ones are absolute
                          // blob-storage URLs.
                          const displayUrl = dek ? docObjectUrls[i] : docUrl;

                          return (
                            <button
                              key={i}
                              disabled={!displayUrl}
                              onClick={() =>
                                displayUrl && setDocPreview({ url: displayUrl, isPdf })
                              }
                              className="group relative overflow-hidden rounded-lg border text-left transition-all hover:ring-2 hover:ring-teal-500 disabled:cursor-progress disabled:opacity-60"
                            >
                              {isPdf ? (
                                <div className="flex h-24 flex-col items-center justify-center bg-red-50 dark:bg-red-950/30">
                                  <svg
                                    className="size-8 text-red-500"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  >
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <path d="M9 15h6" />
                                    <path d="M9 11h6" />
                                  </svg>
                                  <span className="mt-1 text-[0.625rem] font-medium text-red-600">
                                    PDF
                                  </span>
                                </div>
                              ) : displayUrl ? (
                                <img
                                  src={displayUrl}
                                  alt={slotLabel}
                                  className="h-24 w-full object-cover transition-transform group-hover:scale-105"
                                />
                              ) : (
                                <div className="bg-muted h-24 w-full animate-pulse" />
                              )}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                                <span className="rounded bg-white/90 px-1.5 py-0.5 text-[0.625rem] font-medium text-gray-900 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                                  <Trans>View</Trans>
                                </span>
                              </div>
                              <div className="bg-card border-t px-2 py-1">
                                <p className="text-muted-foreground truncate text-[0.625rem] font-medium">
                                  {slotLabel}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 text-center text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                      <Trans>No verification documents uploaded</Trans>
                    </div>
                  )}
                </div>

                {reviewProvider.primaryCountry === "RO" && (
                  <div className="rounded-lg border p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-semibold">
                        <Trans>CMR Registry Lookup</Trans>
                      </h3>
                      <span className="flex items-center gap-2">
                        <button
                          className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-medium hover:bg-zinc-200"
                          title={t`Copy name to clipboard`}
                          onClick={() => {
                            void navigator.clipboard.writeText(
                              `${reviewProvider.lastName} ${reviewProvider.firstName}`,
                            );
                          }}
                        >
                          <Trans>Copy name</Trans>
                        </button>
                        <a
                          href="https://regmed.cmr.ro/registrul-medicilor/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 underline"
                        >
                          <Trans>Open CMR →</Trans>
                        </a>
                      </span>
                    </div>
                    {cmrLoading ? (
                      <div className="text-muted-foreground text-sm">
                        <Trans>Searching CMR registry…</Trans>
                      </div>
                    ) : cmrResult ? (
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">
                            <Trans>Name:</Trans>
                          </span>{" "}
                          <span className="font-medium">{cmrResult.fullName}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            <Trans>Specialty:</Trans>
                          </span>{" "}
                          {cmrResult.specialty || "—"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            <Trans>License:</Trans>
                          </span>{" "}
                          <span className="font-medium">{cmrResult.licenseNo || "—"}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            <Trans>Grade:</Trans>
                          </span>{" "}
                          {cmrResult.grade || "—"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            <Trans>Facility:</Trans>
                          </span>{" "}
                          {[cmrResult.city, cmrResult.county].filter(Boolean).join(", ") || "—"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            <Trans>Active license:</Trans>
                          </span>{" "}
                          {cmrResult.hasActiveLicense ? (
                            <span className="font-medium text-emerald-600">
                              <Trans>Yes</Trans>
                            </span>
                          ) : (
                            <span className="font-medium text-red-500">
                              <Trans>No</Trans>
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted-foreground text-sm">
                        <Trans>No CMR match found for this name.</Trans>
                      </div>
                    )}
                  </div>
                )}

                {reviewProvider.facilityProviders.length > 0 && (
                  <div className="rounded-lg border p-4">
                    <h3 className="mb-2 text-sm font-semibold">
                      <Trans>
                        Affiliated facilities ({reviewProvider.facilityProviders.length})
                      </Trans>
                    </h3>
                    <div className="space-y-2">
                      {reviewProvider.facilityProviders.map((ld, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div>
                            <span className="font-medium">
                              {ld.facility.name?.ro || ld.facility.name?.en || "—"}
                            </span>
                            <span className="text-muted-foreground">
                              {" "}
                              · {ld.facility.addressCity} · {ld.role}
                            </span>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {ld.facility.category}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {reviewProvider.providerServices.length > 0 && (
                  <div className="rounded-lg border p-4">
                    <h3 className="mb-2 text-sm font-semibold">
                      <Trans>Services ({reviewProvider.providerServices.length})</Trans>
                    </h3>
                    <div className="space-y-1">
                      {reviewProvider.providerServices.map((ds, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>
                            {ds.service.name?.ro || ds.service.name?.en || ds.service.code}
                          </span>
                          <div className="flex items-center gap-2">
                            {ds.price && (
                              <span className="font-medium">
                                {Number(ds.price)} {ds.currency}
                              </span>
                            )}
                            {ds.acceptsCas && (
                              <Badge className="bg-green-100 text-xs text-green-700">CAS</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {reviewProvider.providerSpecialties.length > 0 && (
                  <div ref={specialtiesSectionRef} className="rounded-lg border p-4">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                      <Trans>Specialties ({reviewProvider.providerSpecialties.length})</Trans>
                      {reviewProvider.providerSpecialties.some(
                        (s) => s.approvalStatus === "pending",
                      ) && (
                        <Badge className="bg-amber-100 text-[0.625rem] text-amber-700">
                          <Trans>
                            {
                              reviewProvider.providerSpecialties.filter(
                                (s) => s.approvalStatus === "pending",
                              ).length
                            }{" "}
                            pending
                          </Trans>
                        </Badge>
                      )}
                    </h3>
                    <div className="space-y-2">
                      {reviewProvider.providerSpecialties.map((ps) => {
                        const name =
                          ps.specialty.name?.ro || ps.specialty.name?.en || ps.specialty.code;
                        const approving = specialtyActionId === `${ps.specialtyId}:approve`;
                        const rejecting = specialtyActionId === `${ps.specialtyId}:reject`;
                        return (
                          <div
                            key={ps.specialtyId}
                            className="border-border flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{name}</span>
                                {ps.isPrimary && (
                                  <Badge className="bg-teal-100 text-[0.625rem] text-teal-700">
                                    <Trans>primary</Trans>
                                  </Badge>
                                )}
                                {ps.specialty.type === "subspecialty" && (
                                  <Badge variant="outline" className="text-[0.625rem]">
                                    <Trans>sub</Trans>
                                  </Badge>
                                )}
                                {ps.approvalStatus === "approved" && (
                                  <Badge className="bg-green-100 text-[0.625rem] text-green-700">
                                    <Trans>approved</Trans>
                                  </Badge>
                                )}
                                {ps.approvalStatus === "pending" && (
                                  <Badge className="bg-amber-100 text-[0.625rem] text-amber-700">
                                    <Trans>pending</Trans>
                                  </Badge>
                                )}
                                {ps.approvalStatus === "rejected" && (
                                  <Badge className="bg-red-100 text-[0.625rem] text-red-700">
                                    <Trans>rejected</Trans>
                                  </Badge>
                                )}
                              </div>
                              {ps.approvalStatus === "rejected" && ps.rejectionReason && (
                                <p className="text-muted-foreground mt-0.5 text-xs italic">
                                  {ps.rejectionReason}
                                </p>
                              )}
                              {ps.approvalStatus === "approved" && ps.verifiedAt && (
                                <p className="text-muted-foreground mt-0.5 text-xs">
                                  <Trans>
                                    Approved {new Date(ps.verifiedAt).toLocaleDateString()}
                                  </Trans>
                                </p>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5">
                              {ps.approvalStatus !== "approved" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 border-green-300 text-xs text-green-700 hover:bg-green-50"
                                  disabled={approving || rejecting}
                                  onClick={() =>
                                    decideSpecialty(reviewProvider.id, ps.specialtyId, "approve")
                                  }
                                >
                                  {approving ? "..." : <Trans>Approve</Trans>}
                                </Button>
                              )}
                              {ps.approvalStatus !== "rejected" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 border-red-300 text-xs text-red-700 hover:bg-red-50"
                                  disabled={approving || rejecting}
                                  onClick={() => {
                                    const reason =
                                      prompt(t`Reason for rejection (optional):`) ?? undefined;
                                    void decideSpecialty(
                                      reviewProvider.id,
                                      ps.specialtyId,
                                      "reject",
                                      reason,
                                    );
                                  }}
                                >
                                  {rejecting ? "..." : <Trans>Reject</Trans>}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="rounded-lg border p-4">
                  <h3 className="mb-2 text-sm font-semibold">
                    <Trans>Contact</Trans>
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Emails:</Trans>
                      </span>{" "}
                      {reviewProvider.emails.length > 0 ? reviewProvider.emails.join(", ") : "—"}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Video:</Trans>
                      </span>{" "}
                      {reviewProvider.offersVideoConsultation ? t`Yes` : t`No`}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <Trans>Accepts new patients:</Trans>
                      </span>{" "}
                      {reviewProvider.acceptsNewPatients ? t`Yes` : t`No`}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border">
                  <button
                    className="hover:bg-muted/50 flex w-full items-center justify-between p-4 text-left text-sm font-semibold transition-colors"
                    onClick={() => setTimelineOpen(!timelineOpen)}
                  >
                    <span>
                      <Trans>Activity log ({auditLogs.length})</Trans>
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {timelineOpen ? "▲" : "▼"}
                    </span>
                  </button>
                  {timelineOpen && (
                    <div className="border-t px-4 pb-4">
                      {auditLoading ? (
                        <p className="text-muted-foreground py-3 text-center text-xs">
                          <Trans>Loading…</Trans>
                        </p>
                      ) : auditLogs.length === 0 ? (
                        <p className="text-muted-foreground py-3 text-center text-xs">
                          <Trans>No activity recorded yet</Trans>
                        </p>
                      ) : (
                        <div className="relative mt-3 space-y-0">
                          {auditLogs.map((log, i) => {
                            const cfg = AUDIT_ACTION_CONFIG[log.action] || {
                              label: log.action,
                              color: "text-muted-foreground bg-muted",
                              icon: "•",
                            };
                            const details = log.details;
                            const reasons = (details?.reasons as string[]) || [];
                            const customMsg = details?.customMessage as string | undefined;
                            return (
                              <div key={log.id} className="flex gap-3">
                                <div className="flex flex-col items-center">
                                  <div
                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${cfg.color}`}
                                  >
                                    {cfg.icon}
                                  </div>
                                  {i < auditLogs.length - 1 && (
                                    <div className="bg-border w-px grow" />
                                  )}
                                </div>
                                <div className="min-w-0 pb-4">
                                  <div className="flex items-baseline gap-2">
                                    <span className="text-sm font-medium">{cfg.label}</span>
                                    <span className="text-muted-foreground text-xs">
                                      {new Date(log.createdAt).toLocaleDateString(undefined, {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </div>
                                  <div className="text-muted-foreground text-xs">
                                    <Trans>by {log.actorType}</Trans>
                                  </div>
                                  {reasons.length > 0 && (
                                    <div className="mt-1 space-y-0.5">
                                      {reasons.map((r) => (
                                        <div key={r} className="text-xs text-orange-700">
                                          ·{" "}
                                          {NOTIFY_REASON_OPTIONS.find((o) => o.key === r)?.label ||
                                            r}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {customMsg && (
                                    <div className="mt-1 text-xs text-amber-700 italic">
                                      &quot;{customMsg}&quot;
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-orange-200 bg-orange-50/50">
                  <button
                    className="flex w-full items-center justify-between p-4 text-left text-sm font-semibold text-orange-800 transition-colors hover:bg-orange-100/50"
                    onClick={() => setNotifyPanelOpen(!notifyPanelOpen)}
                  >
                    <span>
                      <Trans>Send update request to provider</Trans>
                    </span>
                    <span className="text-xs text-orange-400">{notifyPanelOpen ? "▲" : "▼"}</span>
                  </button>
                  {notifyPanelOpen && (
                    <div className="space-y-3 border-t border-orange-200 p-4">
                      <p className="text-muted-foreground text-xs">
                        <Trans>
                          Select the issues and send a notification + email to the provider.
                        </Trans>
                      </p>
                      <div className="space-y-2">
                        {NOTIFY_REASON_OPTIONS.map((option) => (
                          <label
                            key={option.key}
                            className="group flex cursor-pointer items-start gap-2.5"
                          >
                            <input
                              type="checkbox"
                              checked={notifyReasons.has(option.key)}
                              onChange={(e) => {
                                const next = new Set(notifyReasons);
                                if (e.target.checked) next.add(option.key);
                                else next.delete(option.key);
                                setNotifyReasons(next);
                              }}
                              className="mt-0.5 h-4 w-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                            />
                            <span className="text-foreground text-sm transition-colors group-hover:text-orange-700">
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div>
                        <label className="text-foreground text-xs font-medium">
                          <Trans>Custom message (optional)</Trans>
                        </label>
                        <textarea
                          value={notifyCustomMsg}
                          onChange={(e) => setNotifyCustomMsg(e.target.value)}
                          rows={2}
                          className="bg-background mt-1 block w-full rounded-md border border-orange-200 px-3 py-2 text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-200 focus:outline-none"
                          placeholder={t`Additional details for the provider…`}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          size="sm"
                          className="bg-orange-600 text-white hover:bg-orange-700"
                          disabled={
                            notifySending || (notifyReasons.size === 0 && !notifyCustomMsg.trim())
                          }
                          onClick={() => sendNotification(reviewProvider.id)}
                        >
                          {notifySending ? (
                            <Trans>Sending…</Trans>
                          ) : (
                            <Trans>Send notification & email</Trans>
                          )}
                        </Button>
                        {notifySuccess && (
                          <span className="text-xs font-medium text-emerald-600">
                            <Trans>Sent successfully!</Trans>
                          </span>
                        )}
                        {notifyError && (
                          <span className="text-xs font-medium text-red-600">{notifyError}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                  {reviewProvider.profileStatus === "pending_review" && (
                    <>
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        disabled={actionLoading === reviewProvider.id}
                        onClick={async () => {
                          await suspendProvider(reviewProvider.id);
                          setReviewProvider(null);
                          setCmrResult(null);
                        }}
                      >
                        <Trans>Reject & suspend</Trans>
                      </Button>
                      <Button
                        className="bg-teal-600 text-white hover:bg-teal-700"
                        disabled={actionLoading === reviewProvider.id}
                        onClick={async () => {
                          await approveProvider(reviewProvider.id);
                          setReviewProvider(null);
                          setCmrResult(null);
                        }}
                      >
                        {actionLoading === reviewProvider.id ? (
                          <Trans>Approving…</Trans>
                        ) : (
                          <Trans>Approve & publish</Trans>
                        )}
                      </Button>
                    </>
                  )}
                  {reviewProvider.profileStatus !== "pending_review" && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setReviewProvider(null);
                        setCmrResult(null);
                      }}
                    >
                      <Trans>Close</Trans>
                    </Button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {docPreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setDocPreview(null)}
        >
          <div
            className="bg-card relative max-h-[90vh] max-w-4xl overflow-hidden rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDocPreview(null)}
              className="absolute top-3 right-3 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
              aria-label={t`Close`}
            >
              <span className="text-lg font-bold">✕</span>
            </button>
            <a
              href={docPreview.url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-3 left-3 z-10 rounded-full bg-black/50 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-black/70"
            >
              <Trans>Open in new tab ↗</Trans>
            </a>

            {docPreview.isPdf ? (
              <iframe
                src={docPreview.url}
                title={t`Document preview`}
                className="h-[85vh] w-[min(800px,90vw)]"
              />
            ) : (
              <img src={docPreview.url} alt={t`Document preview`} className="max-h-[85vh] w-auto" />
            )}
          </div>
        </div>
      )}

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Trans>Delete profile</Trans>
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.isClaimed
                ? t`Delete the profile of ${providerName}? The owner is notified. The profile and the facilities only they own are hidden now and erased after 30 days — until then you can undo it with "Cancel deletion".`
                : t`Delete the profile of ${providerName}? Nobody has claimed it. It disappears from the site and search, pending booking requests are closed, and imports from public sources will not bring it back. This cannot be undone from the admin.`}
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && !deleteTarget.isClaimed && (
            <label className="border-border flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3">
              <input
                type="checkbox"
                checked={erasureRequested}
                onChange={(e) => setErasureRequested(e.target.checked)}
                className="mt-0.5 size-4 shrink-0"
              />
              <span className="flex flex-col gap-1">
                <span className="font-medium">
                  <Trans>They asked for the removal</Trans>
                </span>
                <span className="text-muted-foreground text-xs">
                  <Trans>
                    Tick it when the professional asked us (email to dpo@, phone) and you checked it
                    is them. Their email addresses and phone numbers are blocked, and the profile is
                    reduced to what stops imports from bringing it back — the same as the link in
                    the information email. Leave it unticked for a duplicate or wrong data.
                  </Trans>
                </span>
              </span>
            </label>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              className="min-h-[44px]"
              onClick={() => setDeleteTarget(null)}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button variant="destructive" className="min-h-[44px]" onClick={confirmDeleteProvider}>
              <Trans>Delete</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
